import { useQuery } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { WrittenResolutionInput, type WrittenResolution } from '@syndiqia/governance/contracts';
import { errorMessage, useApi } from '../../api';
import { Alert, Disclosure, Empty, Field, Loading, PageHeader, validate, type FieldErrors } from '../../components/ui';
import { useGovernanceMutation } from '../../governance-queries';
import { useI18n } from '../../i18n';
import { useMe, useSyndicate } from '../../session';
import { GovernanceNav } from './GovernanceNav';

/** Written resolutions of the board: drafted, circulated to the directors, adopted when all have signed. */
export function WrittenPage() {
  const { t } = useI18n();
  const s = useSyndicate();
  const api = useApi();
  const g = t.governance;
  const list = useQuery({ queryKey: [s.tenantId, 'governance', 'written'], queryFn: () => api<WrittenResolution[]>(`${s.api}/governance/written`), enabled: s.can('governance.read') });
  if (!s.can('governance.read')) return <Alert tone="info">{g.boardOnly}</Alert>;
  return (
    <>
      <PageHeader title={g.title} intro={g.written.intro} actions={s.can('governance.manage') && <Disclosure label={g.written.new}>{(close) => <WrittenForm onDone={close} />}</Disclosure>} />
      <GovernanceNav />
      {list.isPending && <Loading label={t.loading} />}
      {list.isError && <Alert>{errorMessage(list.error, t)}</Alert>}
      {list.data?.length === 0 && <Empty>{g.written.none}</Empty>}
      {list.data?.map((r) => <WrittenCard key={r.id} resolution={r} />)}
    </>
  );
}

function WrittenForm({ onDone, resolution }: { onDone: () => void; resolution?: WrittenResolution }) {
  const { t } = useI18n();
  const w = t.governance.written;
  const [values, setValues] = useState({ title: resolution?.title ?? '', resolution: resolution?.resolution ?? '' });
  const [errors, setErrors] = useState<FieldErrors>({});
  const save = useGovernanceMutation<WrittenResolutionInput, WrittenResolution>((api, base, input) =>
    resolution ? api(`${base}/governance/written/${resolution.id}`, { method: 'PATCH', body: input }) : api(`${base}/governance/written`, { method: 'POST', body: input }),
  );
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const result = validate(WrittenResolutionInput, values);
    setErrors(result.errors ?? {});
    if (result.data) save.mutate(result.data, { onSuccess: onDone });
  };
  return (
    <form className="form" onSubmit={submit} noValidate>
      <Field label={w.title} error={errors.title}>
        <input value={values.title} onChange={(e) => setValues({ ...values, title: e.target.value })} autoFocus />
      </Field>
      <Field label={w.text} error={errors.resolution}>
        <textarea rows={6} value={values.resolution} placeholder={w.textPlaceholder} onChange={(e) => setValues({ ...values, resolution: e.target.value })} />
      </Field>
      {save.isError && <Alert>{errorMessage(save.error, t)}</Alert>}
      <div className="form-actions">
        <button type="submit" className="button button-primary" disabled={save.isPending}>
          {t.common.save}
        </button>
        <button type="button" className="button" onClick={onDone}>
          {t.common.cancel}
        </button>
      </div>
    </form>
  );
}

function WrittenCard({ resolution: r }: { resolution: WrittenResolution }) {
  const { t, date } = useI18n();
  const s = useSyndicate();
  const me = useMe();
  const w = t.governance.written;
  const [editing, setEditing] = useState(false);
  const circulate = useGovernanceMutation<void, WrittenResolution>((api, base) => api(`${base}/governance/written/${r.id}/circulate`, { method: 'POST' }));
  const sign = useGovernanceMutation<void, WrittenResolution>((api, base) => api(`${base}/governance/written/${r.id}/sign`, { method: 'POST' }));
  const cancel = useGovernanceMutation<void, WrittenResolution>((api, base) => api(`${base}/governance/written/${r.id}/cancel`, { method: 'POST' }));
  const signed = r.signatures.filter((x) => x.signedAt).length;
  const mine = r.signatures.find((x) => x.userId === me.data?.user.id);
  const error = circulate.error ?? sign.error ?? cancel.error;
  const day = (iso: string) => date(iso.slice(0, 10));

  return (
    <section className="panel written">
      <p>
        <span className={`badge badge-written-${r.status}`}>{w.statuses[r.status]}</span>{' '}
        {r.status === 'adopted' && r.adoptedAt && <span className="muted small">{w.adoptedOn(day(r.adoptedAt))}</span>}
        {r.status === 'circulating' && <span className="muted small">{w.progress(signed, r.signatures.length)}</span>}
      </p>
      {editing ? (
        <WrittenForm resolution={r} onDone={() => setEditing(false)} />
      ) : (
        <>
          <h2>{r.title}</h2>
          <p className="resolution message-body">{r.resolution}</p>
        </>
      )}
      {r.signatures.length > 0 && (
        <ul className="compact-list">
          {r.signatures.map((x) => (
            <li key={x.userId}>
              <span>{x.name}</span>
              <span className={x.signedAt ? 'small' : 'muted small'}>{x.signedAt ? `✓ ${w.signed(day(x.signedAt))}` : w.waiting}</span>
            </li>
          ))}
        </ul>
      )}
      {r.status === 'circulating' && mine && !mine.signedAt && <Alert tone="info">{w.yourTurn}</Alert>}
      {error && <Alert>{errorMessage(error, t)}</Alert>}
      <div className="form-actions">
        {r.status === 'circulating' && mine && !mine.signedAt && s.can('governance.approve') && (
          <button type="button" className="button button-primary" disabled={sign.isPending} onClick={() => window.confirm(w.confirmSign) && sign.mutate()}>
            {w.sign}
          </button>
        )}
        {r.status === 'draft' && s.can('governance.manage') && !editing && (
          <>
            <button type="button" className="button button-primary" disabled={circulate.isPending} onClick={() => window.confirm(w.confirmCirculate) && circulate.mutate()}>
              {w.circulate}
            </button>
            <button type="button" className="button" onClick={() => setEditing(true)}>
              {t.governance.edit}
            </button>
          </>
        )}
        {(r.status === 'draft' || r.status === 'circulating') && s.can('governance.manage') && !editing && (
          <button type="button" className="link-button small" disabled={cancel.isPending} onClick={() => window.confirm(w.confirmCancel) && cancel.mutate()}>
            {w.cancel}
          </button>
        )}
      </div>
    </section>
  );
}
